import { h, clear, toast, formatDate } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Check, RefreshCw, ChevronLeft, ChevronRight } from 'lucide';
import { AuditAPI, BranchAPI } from '../services/endpoints.js';

const TYPES = [
  { value: '', label: 'Todos los tipos de evento' },
  { value: 'user_created_by_admin', label: 'Usuario creado por administrador' },
  { value: 'user_disabled', label: 'Usuario deshabilitado' },
  { value: 'password_reset', label: 'Restablecimiento de contraseña' },
  { value: 'registration_submitted', label: 'Registro pendiente' },
  { value: 'registration_approved', label: 'Registro aprobado' },
  { value: 'registration_rejected', label: 'Registro rechazado' },
];

const TYPE_LABELS = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));

const roleLabel = (r) => r === 'admin' ? 'administrador' : r === 'instructor' ? 'instructor' : 'beneficiario';

export const AuditPage = async () => {
  const view = document.getElementById('view');
  clear(view);

  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Auditoría y trazabilidad'),
  ));
  view.appendChild(h('p', { class: 'page-subtitle' },
    'Registro de eventos de toda la plataforma. Cada cambio de estado deja aquí un evento trazable.'
  ));

  const filtersCard = h('div', { class: 'card' });
  view.appendChild(filtersCard);
  const logCard = h('div', { class: 'card' });
  view.appendChild(logCard);

  const state = { type: '', branch_id: '', from: '', to: '', limit: 200, offset: 0, total: 0 };

  const typeSelect = h('select', { name: 'type' },
    ...TYPES.map((t) => h('option', { value: t.value }, t.label)),
  );
  const branchSelect = h('select', { name: 'branch_id' });
  const fromInput = h('input', { name: 'from', type: 'datetime-local' });
  const toInput = h('input', { name: 'to', type: 'datetime-local' });
  const resetBtn = h('button', { class: 'btn btn-ghost', onclick: () => { reset(); refresh(); } }, h('span', { html: iconSvg(RefreshCw, 16) }), 'Restablecer');

  filtersCard.appendChild(h('div', { class: 'row gap-sm', style: { flexWrap: 'wrap', alignItems: 'flex-end' } },
    filterField('Tipo de evento', typeSelect),
    h('div', {},
      h('label', {}, 'Sucursal'),
      branchSelect,
    ),
    h('div', {},
      h('label', {}, 'Desde'),
      fromInput,
    ),
    h('div', {},
      h('label', {}, 'Hasta'),
      toInput,
    ),
    h('div', { class: 'btn-row' },
      h('button', { class: 'btn', onclick: () => refresh() }, h('span', { html: iconSvg(Check, 16) }), 'Aplicar'),
      resetBtn,
    ),
  ));

  typeSelect.onchange = (e) => { state.type = e.target.value; };
  branchSelect.onchange = (e) => { state.branch_id = e.target.value; };
  fromInput.onchange = (e) => { state.from = e.target.value; };
  toInput.onchange = (e) => { state.to = e.target.value; };

  function reset() {
    state.type = '';
    state.branch_id = '';
    state.from = '';
    state.to = '';
    state.offset = 0;
    typeSelect.value = '';
    branchSelect.value = '';
    fromInput.value = '';
    toInput.value = '';
  }

  function toLocalIso(local) {
    if (!local) return null;
    const d = new Date(local);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }

  function filterField(label, control) {
    return h('div', {}, h('label', {}, label), control);
  }

  async function loadLog() {
    clear(logCard);
    logCard.appendChild(h('h2', {}, 'Registro de eventos'));
    try {
      const params = { limit: state.limit, offset: state.offset };
      if (state.type) params.type = state.type;
      if (state.branch_id) params.branch_id = state.branch_id;
      if (state.from) params.from = toLocalIso(state.from);
      if (state.to) params.to = toLocalIso(state.to);
      const res = await AuditAPI.notifications(params);
      const { items, total } = res.data;
      state.total = total;

      logCard.appendChild(h('div', { class: 'muted text-sm mb-8' },
        `Mostrando ${items.length} de ${total} eventos.`
      ));

      if (!items.length) {
        logCard.appendChild(h('div', { class: 'table-empty' }, 'Ningún evento coincide con los filtros actuales.'));
        return;
      }

      const table = h('table', { class: 'table' },
        h('thead', {}, h('tr', {},
          h('th', {}, 'Cuándo'),
          h('th', {}, 'Tipo'),
          h('th', {}, 'Destinatario'),
          h('th', {}, 'Sucursal'),
          h('th', {}, 'Mensaje'),
        )),
        h('tbody', {}, ...items.map((n) => h('tr', {},
          h('td', { class: 'text-sm muted' }, formatDate(n.created_at)),
          h('td', {}, h('span', { class: 'badge' }, TYPE_LABELS[n.type] || n.type)),
          h('td', {},
            h('strong', {}, n.user_name || `Usuario #${n.user_id}`),
            h('div', { class: 'muted text-sm' },
              [n.user_email, n.user_role ? `(${roleLabel(n.user_role)})` : ''].filter(Boolean).join(' ')
            ),
          ),
          h('td', {}, n.branch_name || '—'),
          h('td', {}, n.title, h('div', { class: 'muted text-sm' }, n.message)),
        )))
      );
      logCard.appendChild(h('div', { class: 'table-wrap' }, table));

      // Pagination.
      const totalPages = Math.max(1, Math.ceil(total / state.limit));
      const currentPage = Math.floor(state.offset / state.limit) + 1;
      const pager = h('div', { class: 'row-between mt-16' },
        h('div', { class: 'muted text-sm' }, `Página ${currentPage} de ${totalPages}`),
        h('div', { class: 'btn-row' },
          h('button', { class: 'btn btn-ghost btn-sm', disabled: state.offset === 0 ? '' : null, onclick: () => { state.offset = Math.max(0, state.offset - state.limit); loadLog(); } }, h('span', { html: iconSvg(ChevronLeft, 14) }), 'Anterior'),
          h('button', { class: 'btn btn-ghost btn-sm', disabled: state.offset + state.limit >= total ? '' : null, onclick: () => { state.offset += state.limit; loadLog(); } }, 'Siguiente', h('span', { html: iconSvg(ChevronRight, 14) })),
        ),
      );
      logCard.appendChild(pager);
    } catch (err) {
      toast(err.message, 'error');
    }
  }

  async function refresh() {
    state.offset = 0;
    await loadLog();
  }

  // Populate branch dropdown.
  try {
    const res = await BranchAPI.list({ include_inactive: '0' });
    branchSelect.appendChild(h('option', { value: '' }, 'Todas las sucursales'));
    for (const b of res.data || []) {
      branchSelect.appendChild(h('option', { value: b.id }, b.name));
    }
  } catch (err) {
    // ignore
  }

  await refresh();
};
